/**
 * FloorArrowManager - Creates floor arrow decals for indoor navigation
 * Renders flat arrow indicators on the floor pointing toward destinations
 */

import { Logger } from '../utils/Logger';
import { Scene } from '@babylonjs/core/scene';
import type { Observer } from '@babylonjs/core/Misc/observable';

const logger = Logger.getLogger('FloorArrow');
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Effect } from '@babylonjs/core/Materials/effect';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { ActionManager } from '@babylonjs/core/Actions/actionManager';
import { ExecuteCodeAction } from '@babylonjs/core/Actions/directActions';
import { Animation } from '@babylonjs/core/Animations/animation';

import type { HotspotConfig } from '../types';

// Register floor arrow shaders
Effect.ShadersStore["floorArrowVertexShader"] = `
precision highp float;

attribute vec3 position;
attribute vec2 uv;

uniform mat4 worldViewProjection;

varying vec2 vUV;

void main(void) {
    gl_Position = worldViewProjection * vec4(position, 1.0);
    vUV = uv;
}
`;

Effect.ShadersStore["floorArrowFragmentShader"] = `
precision highp float;

varying vec2 vUV;

uniform vec3 arrowColor;
uniform float time;
uniform float isHovered;

// Signed distance to a box with rounded corners
float sdRoundBox(vec2 p, vec2 size, float radius) {
    vec2 q = abs(p) - size + radius;
    return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - radius;
}

// Signed distance to a triangle
float sdTriangle(vec2 p, vec2 p0, vec2 p1, vec2 p2) {
    vec2 e0 = p1 - p0, e1 = p2 - p1, e2 = p0 - p2;
    vec2 v0 = p - p0, v1 = p - p1, v2 = p - p2;

    vec2 pq0 = v0 - e0 * clamp(dot(v0, e0) / dot(e0, e0), 0.0, 1.0);
    vec2 pq1 = v1 - e1 * clamp(dot(v1, e1) / dot(e1, e1), 0.0, 1.0);
    vec2 pq2 = v2 - e2 * clamp(dot(v2, e2) / dot(e2, e2), 0.0, 1.0);

    float s = sign(e0.x * e2.y - e0.y * e2.x);
    vec2 d = min(min(
        vec2(dot(pq0, pq0), s * (v0.x * e0.y - v0.y * e0.x)),
        vec2(dot(pq1, pq1), s * (v1.x * e1.y - v1.y * e1.x))),
        vec2(dot(pq2, pq2), s * (v2.x * e2.y - v2.y * e2.x)));

    return -sqrt(d.x) * sign(d.y);
}

// Signed distance to an elegant connected arrow
float sdArrow(vec2 p) {
    // Normalize UV to -1 to 1 range, centered
    vec2 uv = (p - 0.5) * 2.0;

    // Arrow dimensions - elegant proportions
    float headWidth = 0.5;       // Half-width of arrowhead at base
    float tailWidth = 0.16;      // Half-width of the tail/stem
    float cornerRadius = 0.04;   // Rounded corners on tail

    // Key Y positions
    float tipY = 0.7;            // Arrow tip
    float headBaseY = 0.15;      // Where head meets tail
    float tailBottomY = -0.75;   // Bottom of tail
    float tailOverlap = 0.12;    // How far tail extends into triangle for seamless join

    // Arrowhead triangle
    vec2 tip = vec2(0.0, tipY);
    vec2 headLeft = vec2(-headWidth, headBaseY);
    vec2 headRight = vec2(headWidth, headBaseY);
    float headDist = sdTriangle(uv, tip, headLeft, headRight);

    // Tail rectangle - extends into triangle for seamless connection
    float tailTopY = headBaseY + tailOverlap;
    vec2 tailCenter = vec2(0.0, (tailTopY + tailBottomY) * 0.5);
    vec2 tailSize = vec2(tailWidth, (tailTopY - tailBottomY) * 0.5);
    float tailDist = sdRoundBox(uv - tailCenter, tailSize, cornerRadius);

    // Simple union - min of both distances
    return min(headDist, tailDist);
}

void main(void) {
    float dist = sdArrow(vUV);

    // Subtle breathing animation
    float pulse = sin(time * 1.2) * 0.015 + 0.985;

    // High-quality anti-aliased edge
    float edgeWidth = fwidth(dist) * 1.2;
    float arrowAlpha = 1.0 - smoothstep(-edgeWidth, edgeWidth, dist);

    // Elegant soft glow (expands on hover)
    float glowSize = mix(0.08, 0.14, isHovered);
    float glowIntensity = mix(0.25, 0.4, isHovered);
    float glow = (1.0 - smoothstep(0.0, glowSize, dist)) * glowIntensity;

    // Combine layers
    float totalAlpha = max(arrowAlpha, glow) * pulse;

    // Brightness boost on hover
    float brightness = mix(1.0, 1.15, isHovered);
    vec3 finalColor = arrowColor * brightness;

    // Discard fully transparent pixels
    if (totalAlpha < 0.01) discard;

    gl_FragColor = vec4(finalColor, totalAlpha);
}
`;

interface FloorArrow {
    mesh: Mesh;
    collisionMesh: Mesh; // Invisible collision mesh for reliable picking
    material: ShaderMaterial | StandardMaterial;
    hotspotId: string;
    targetStageId: string | undefined; // Optional in editor mode (destination may not be set yet)
    isHovered: boolean;
    isCustomIcon: boolean;
    restScale: number;
    hoverScale: number;
    baseSize: number;
    glowColor: Vector3;
}

export class FloorArrowManager {
    private scene: Scene;
    private parentNode: TransformNode | null = null;
    private arrows: Map<string, FloorArrow> = new Map();
    private animationObserver: Observer<Scene> | null = null;
    private startTime: number;
    private isEditor: boolean;

    // Callbacks
    public onArrowClick?: (hotspotId: string, targetStageId: string | undefined) => void;
    public onArrowHover?: (hotspotId: string) => void;
    public onArrowHoverExit?: (hotspotId: string) => void;

    // Configuration
    private readonly ARROW_SIZE = 180; // Size in scene units (larger for visibility in 360 scenes)
    private readonly FLOOR_OFFSET = 1; // Offset above hotspot position to prevent z-fighting
    private readonly DISTANCE_SCALE = 0.75; // Bring arrow closer to camera (0.75 = 75% of hotspot distance)

    constructor(scene: Scene, isEditor: boolean = false) {
        this.scene = scene;
        this.isEditor = isEditor;
        this.startTime = performance.now();
        this.setupAnimation();
    }

    /**
     * Set the parent node for all arrow meshes (for skybox rotation grouping)
     */
    setParentNode(parent: TransformNode | null): void {
        this.parentNode = parent;
        // Re-parent any existing arrows
        this.arrows.forEach((arrow) => {
            arrow.mesh.parent = parent;
        });
    }

    /**
     * Setup animation loop for shader time updates
     */
    private setupAnimation(): void {
        this.animationObserver = this.scene.onBeforeRenderObservable.add(() => {
            const currentTime = (performance.now() - this.startTime) / 1000;

            this.arrows.forEach((arrow) => {
                // Only update time uniform for shader materials (not custom icons)
                if (!arrow.isCustomIcon && arrow.material instanceof ShaderMaterial) {
                    arrow.material.setFloat("time", currentTime);
                }
            });
        });
    }

    /**
     * Create a floor arrow for a navigation hotspot
     */
    createArrow(hotspot: HotspotConfig, _targetPosition?: Vector3): void {
        // Check for target scene ID (support both new and legacy field names)
        const targetId = hotspot.targetSceneId || hotspot.targetStageId;
        // In viewer mode, require a destination. In editor mode, show arrows even without destination
        // (user may not have set destination yet - arrow still needs to be visible for positioning)
        if (!targetId && !this.isEditor) {
            logger.warn(`Cannot create arrow for hotspot ${hotspot.id} - no targetSceneId/targetStageId`);
            return;
        }

        // Prevent duplicate arrows for the same hotspot
        if (this.arrows.has(hotspot.id)) {
            logger.warn(`Arrow for hotspot ${hotspot.id} already exists, skipping duplicate`);
            return;
        }

        const hasCustomIcon = !!hotspot.customIconUrl;
        logger.debug(`Creating floor arrow for hotspot ${hotspot.id}${hasCustomIcon ? ' (custom icon)' : ''}`);

        // Calculate size - use hotspot.size as base, apply portalRestSize as multiplier
        const baseSize = hotspot.size || this.ARROW_SIZE;
        const restScale = hotspot.portalRestSize ?? 1.0;
        const hoverScale = hotspot.portalHoverSize ?? 1.2; // Default hover is 20% larger

        // Create horizontal plane with base size (scale applied via mesh scaling)
        const arrow = CreatePlane(
            `floorArrow_${hotspot.id}`,
            {
                size: baseSize,
                sideOrientation: Mesh.DOUBLESIDE,
            },
            this.scene
        );

        // Apply rest scale to mesh
        arrow.scaling = new Vector3(restScale, restScale, restScale);

        // Position arrow based on mode:
        // - Editor: Use exact config position (for drag-to-reposition to work correctly)
        // - Viewer: Scale toward origin (brings arrow closer to camera for better visibility)
        let hotspotPos: Vector3;
        if (this.isEditor) {
            // Editor mode: exact position from config (matches how HotspotComponent positions spheres)
            hotspotPos = new Vector3(
                hotspot.position.x,
                hotspot.position.y,
                hotspot.position.z
            );
        } else {
            // Viewer mode: scaled position (closer to camera)
            hotspotPos = new Vector3(
                hotspot.position.x * this.DISTANCE_SCALE,
                hotspot.position.y * this.DISTANCE_SCALE + this.FLOOR_OFFSET,
                hotspot.position.z * this.DISTANCE_SCALE
            );
        }
        arrow.position = hotspotPos;

        // Rotate to lie flat on floor (rotate 90° around X axis)
        arrow.rotation.x = Math.PI / 2;

        // Calculate direction - ALWAYS start with outward direction from origin
        // This makes arrows naturally point "away" based on their position in the 360 scene
        // atan2(x, z) gives angle from +Z axis toward +X
        const outwardAngle = Math.atan2(hotspot.position.x, hotspot.position.z);

        // Add user offset (arrowDirection) if specified
        // 0° = arrow points toward camera/center (inward)
        // 180° = arrow points away from camera (outward)
        const userOffset = hotspot.arrowDirection !== undefined
            ? (hotspot.arrowDirection * Math.PI) / 180
            : 0;

        // After rotation.x = π/2, the arrow lies flat on the floor
        // outwardAngle orients based on position, userOffset adds manual adjustment
        // No base π offset - 0° means pointing toward camera
        const finalAngle = outwardAngle + userOffset;

        logger.debug(`Direction for ${hotspot.id}:`, {
            outwardAngle: `${(outwardAngle * 180 / Math.PI).toFixed(1)}°`,
            userOffset: `${hotspot.arrowDirection || 0}°`,
            finalAngle: `${(finalAngle * 180 / Math.PI).toFixed(1)}°`
        });

        // Apply direction rotation
        arrow.rotation.y = finalAngle;

        // Parent to scene content parent if set (for skybox rotation grouping)
        if (this.parentNode) {
            arrow.parent = this.parentNode;
        }

        // Render in appropriate group
        arrow.renderingGroupId = 1;

        // Parse glow color - default to cyan (#00bfff) like other portals
        const glowColor = this.parseColor(hotspot.portalColor || '#00bfff');
        let material: ShaderMaterial | StandardMaterial;

        if (hasCustomIcon) {
            // Custom icon mode - use StandardMaterial with texture
            material = this.createCustomIconMaterial(hotspot.id, hotspot.customIconUrl!, glowColor);
        } else {
            // Default shader arrow
            material = this.createShaderMaterial(hotspot.id, glowColor);
        }

        arrow.material = material;

        // The visual arrow uses alpha discard which makes some pixels non-pickable.
        // Create an invisible collision plane that covers the entire mesh for reliable picking.
        const collisionPlane = CreatePlane(
            `floorArrowCollision_${hotspot.id}`,
            {
                size: baseSize * 1.1, // Slightly larger for easier picking
                sideOrientation: Mesh.DOUBLESIDE,
            },
            this.scene
        );
        collisionPlane.parent = arrow; // Attach to arrow so it moves with it
        collisionPlane.position = new Vector3(0, 0.01, 0); // Slightly above arrow to ensure it's picked first

        // IMPORTANT: scene.pick() by default skips invisible meshes!
        // Make the collision plane visible but with a fully transparent material
        collisionPlane.isVisible = true;
        collisionPlane.isPickable = true;
        collisionPlane.renderingGroupId = 1;

        // Create transparent material so the collision plane is pickable but not visible
        const collisionMaterial = new StandardMaterial(`floorArrowCollisionMat_${hotspot.id}`, this.scene);
        collisionMaterial.alpha = 0;
        collisionMaterial.disableLighting = true;
        collisionPlane.material = collisionMaterial;

        // The visual arrow doesn't need to be pickable since collision plane handles it
        arrow.isPickable = false;

        // Store hotspot config in collision mesh metadata for editor dragging system
        if (this.isEditor) {
            collisionPlane.metadata = { config: hotspot, visualMesh: arrow };
            logger.debug(`Collision plane created for ${hotspot.id}:`, {
                name: collisionPlane.name,
                isPickable: collisionPlane.isPickable,
                isVisible: collisionPlane.isVisible,
                hasMetadata: !!collisionPlane.metadata,
                metadataConfigId: collisionPlane.metadata?.config?.id,
                parentName: collisionPlane.parent?.name,
            });
        }

        // Add click and hover handlers (only in viewer mode, not editor)
        // Use collision plane for reliable interaction detection
        if (!this.isEditor) {
            collisionPlane.actionManager = new ActionManager(this.scene);

            // Hover enter
            collisionPlane.actionManager.registerAction(
                new ExecuteCodeAction(ActionManager.OnPointerOverTrigger, () => {
                    this.scene.hoverCursor = 'pointer';
                    this.showHover(hotspot.id);
                })
            );

            // Hover exit
            collisionPlane.actionManager.registerAction(
                new ExecuteCodeAction(ActionManager.OnPointerOutTrigger, () => {
                    this.scene.hoverCursor = 'default';
                    this.hideHover(hotspot.id);
                })
            );

            // Click
            collisionPlane.actionManager.registerAction(
                new ExecuteCodeAction(ActionManager.OnPickTrigger, () => {
                    logger.debug(`Arrow clicked: ${hotspot.id}`);
                    this.handleClick(hotspot.id);
                })
            );
        }

        // Store arrow data with size info for hover animation
        this.arrows.set(hotspot.id, {
            mesh: arrow,
            collisionMesh: collisionPlane,
            material: material,
            hotspotId: hotspot.id,
            targetStageId: targetId, // Use resolved target ID (supports both new and legacy field names)
            isHovered: false,
            isCustomIcon: hasCustomIcon,
            restScale: restScale,
            hoverScale: hoverScale,
            baseSize: baseSize,
            glowColor: glowColor,
        });

        // Ensure arrow is visible
        arrow.visibility = 1;
        arrow.isVisible = true;

        logger.debug(`Arrow created for ${hotspot.id}:`, {
            direction: `${(finalAngle * 180 / Math.PI).toFixed(1)}°`,
            position: `(${arrow.position.x.toFixed(1)}, ${arrow.position.y.toFixed(1)}, ${arrow.position.z.toFixed(1)})`,
            size: baseSize,
            restScale: restScale,
            hoverScale: hoverScale,
            customIcon: hasCustomIcon,
            color: hotspot.portalColor || '#00bfff'
        });
    }

    /**
     * Create shader material for default arrow
     */
    private createShaderMaterial(hotspotId: string, color: Vector3): ShaderMaterial {
        const material = new ShaderMaterial(
            `floorArrowMaterial_${hotspotId}`,
            this.scene,
            {
                vertex: "floorArrow",
                fragment: "floorArrow",
            },
            {
                attributes: ["position", "uv"],
                uniforms: ["worldViewProjection", "arrowColor", "time", "isHovered"],
            }
        );

        material.setVector3("arrowColor", color);
        material.setFloat("time", 0);
        material.setFloat("isHovered", 0);

        // Enable transparency
        material.transparencyMode = 3; // MATERIAL_ALPHABLEND
        material.backFaceCulling = false;

        return material;
    }

    /**
     * Create standard material with custom icon texture
     */
    private createCustomIconMaterial(hotspotId: string, iconUrl: string, glowColor: Vector3): StandardMaterial {
        const material = new StandardMaterial(`floorArrowCustomMaterial_${hotspotId}`, this.scene);

        // Load custom icon texture
        const texture = new Texture(iconUrl, this.scene);
        texture.hasAlpha = true;

        material.diffuseTexture = texture;
        material.emissiveTexture = texture; // Make it glow
        material.useAlphaFromDiffuseTexture = true;
        material.transparencyMode = 2; // MATERIAL_ALPHATEST for crisp edges

        // Apply glow color as emissive tint
        material.emissiveColor = new Color3(glowColor.x, glowColor.y, glowColor.z);
        material.diffuseColor = new Color3(glowColor.x, glowColor.y, glowColor.z);

        material.backFaceCulling = false;
        material.disableLighting = true; // Icon should be visible regardless of scene lighting

        return material;
    }

    /**
     * Show hover effect for an arrow
     */
    showHover(hotspotId: string): void {
        const arrow = this.arrows.get(hotspotId);
        if (arrow) {
            arrow.isHovered = true;

            // Update shader uniform for shader-based arrows
            if (!arrow.isCustomIcon && arrow.material instanceof ShaderMaterial) {
                arrow.material.setFloat("isHovered", 1);
            }

            // For custom icons, brighten the emissive color
            if (arrow.isCustomIcon && arrow.material instanceof StandardMaterial) {
                const brightColor = new Color3(
                    Math.min(1, arrow.glowColor.x * 1.3),
                    Math.min(1, arrow.glowColor.y * 1.3),
                    Math.min(1, arrow.glowColor.z * 1.3)
                );
                arrow.material.emissiveColor = brightColor;
            }

            // Animate scale from rest to hover
            this.animateScale(arrow.mesh, arrow.restScale, arrow.hoverScale);

            if (this.onArrowHover) {
                this.onArrowHover(hotspotId);
            }
        }
    }

    /**
     * Hide hover effect for an arrow
     */
    hideHover(hotspotId: string): void {
        const arrow = this.arrows.get(hotspotId);
        if (arrow) {
            arrow.isHovered = false;

            // Update shader uniform for shader-based arrows
            if (!arrow.isCustomIcon && arrow.material instanceof ShaderMaterial) {
                arrow.material.setFloat("isHovered", 0);
            }

            // For custom icons, restore original emissive color
            if (arrow.isCustomIcon && arrow.material instanceof StandardMaterial) {
                arrow.material.emissiveColor = new Color3(
                    arrow.glowColor.x,
                    arrow.glowColor.y,
                    arrow.glowColor.z
                );
            }

            // Animate scale from hover back to rest
            this.animateScale(arrow.mesh, arrow.hoverScale, arrow.restScale);

            if (this.onArrowHoverExit) {
                this.onArrowHoverExit(hotspotId);
            }
        }
    }

    /**
     * Animate mesh scale smoothly between two values
     */
    private animateScale(mesh: Mesh, fromScale: number, toScale: number): void {
        // Stop any existing scale animations on this mesh
        this.scene.stopAnimation(mesh, 'scaling');

        const animation = new Animation(
            'scaleAnimation',
            'scaling',
            60, // 60 fps
            Animation.ANIMATIONTYPE_VECTOR3,
            Animation.ANIMATIONLOOPMODE_CONSTANT
        );

        const keys = [
            { frame: 0, value: new Vector3(fromScale, fromScale, fromScale) },
            { frame: 10, value: new Vector3(toScale, toScale, toScale) } // 10 frames = ~167ms at 60fps
        ];

        animation.setKeys(keys);
        mesh.animations = [animation];
        this.scene.beginAnimation(mesh, 0, 10, false);
    }

    /**
     * Handle click on an arrow
     */
    handleClick(hotspotId: string): void {
        const arrow = this.arrows.get(hotspotId);
        if (arrow && this.onArrowClick) {
            this.onArrowClick(arrow.hotspotId, arrow.targetStageId);
        }
    }

    /**
     * Get arrow mesh by hotspot ID
     */
    getArrowMesh(hotspotId: string): Mesh | null {
        return this.arrows.get(hotspotId)?.mesh || null;
    }

    /**
     * Check if a mesh is a floor arrow
     */
    isFloorArrow(mesh: Mesh): boolean {
        return mesh.name.startsWith('floorArrow_');
    }

    /**
     * Get hotspot ID from arrow mesh
     */
    getHotspotIdFromMesh(mesh: Mesh): string | null {
        if (!this.isFloorArrow(mesh)) return null;
        return mesh.name.replace('floorArrow_', '');
    }

    /**
     * Update arrow color
     */
    setArrowColor(hotspotId: string, hexColor: string): void {
        const arrow = this.arrows.get(hotspotId);
        if (!arrow) return;

        const color = this.parseColor(hexColor);
        arrow.glowColor = color;

        if (!arrow.isCustomIcon && arrow.material instanceof ShaderMaterial) {
            arrow.material.setVector3('arrowColor', color);
        } else if (arrow.isCustomIcon && arrow.material instanceof StandardMaterial) {
            arrow.material.emissiveColor = new Color3(color.x, color.y, color.z);
            arrow.material.diffuseColor = new Color3(color.x, color.y, color.z);
        }

        logger.debug(`Arrow ${hotspotId} color updated to ${hexColor}`);
    }

    /**
     * Update arrow rest scale
     */
    setArrowRestScale(hotspotId: string, scale: number): void {
        const arrow = this.arrows.get(hotspotId);
        if (!arrow) return;

        arrow.restScale = scale;
        // Apply immediately if not hovered
        if (!arrow.isHovered) {
            arrow.mesh.scaling = new Vector3(scale, scale, scale);
        }

        logger.debug(`Arrow ${hotspotId} rest scale updated to ${scale}`);
    }

    /**
     * Update arrow hover scale
     */
    setArrowHoverScale(hotspotId: string, scale: number): void {
        const arrow = this.arrows.get(hotspotId);
        if (!arrow) return;

        arrow.hoverScale = scale;
        // Apply immediately if hovered
        if (arrow.isHovered) {
            arrow.mesh.scaling = new Vector3(scale, scale, scale);
        }

        logger.debug(`Arrow ${hotspotId} hover scale updated to ${scale}`);
    }

    /**
     * Remove a specific arrow
     */
    removeArrow(hotspotId: string): void {
        const arrow = this.arrows.get(hotspotId);
        if (arrow) {
            arrow.collisionMesh.dispose();
            arrow.mesh.dispose();
            arrow.material.dispose();
            this.arrows.delete(hotspotId);
            logger.debug(`Removed arrow for ${hotspotId}`);
        }
    }

    /**
     * Parse hex color string to Vector3
     */
    private parseColor(hex: string): Vector3 {
        hex = hex.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16) / 255;
        const g = parseInt(hex.substring(2, 4), 16) / 255;
        const b = parseInt(hex.substring(4, 6), 16) / 255;
        return new Vector3(r, g, b);
    }

    /**
     * Dispose all arrows and cleanup
     */
    dispose(): void {
        logger.debug(`Disposing ${this.arrows.size} floor arrows`);

        this.arrows.forEach((arrow) => {
            arrow.collisionMesh.dispose();
            arrow.mesh.dispose();
            arrow.material.dispose();
        });
        this.arrows.clear();

        if (this.animationObserver) {
            this.scene.onBeforeRenderObservable.remove(this.animationObserver);
            this.animationObserver = null;
        }
    }
}
